export interface LiveOdds {
  gameId: string;
  competitionId: string;
  spread?: {
    home: {
      line: number;
      odds: number;
    };
    away: {
      line: number;
      odds: number;
    };
  };
  moneyline?: {
    home: number;
    away: number;
  };
  total?: {
    over: {
      line: number;
      odds: number;
    };
    under: {
      line: number;
      odds: number;
    };
  };
}

interface ESPNOddsResponse {
  items?: Array<{
    provider?: {
      name: string;
    };
    details?: string;
    overUnder?: number;
    spread?: number;
    homeTeamOdds?: {
      favorite?: boolean;
      underdog?: boolean;
      moneyLine?: number;
      spreadOdds?: number;
      team?: {
        id: string;
      };
    };
    awayTeamOdds?: {
      favorite?: boolean;
      underdog?: boolean;
      moneyLine?: number;
      spreadOdds?: number;
      team?: {
        id: string;
      };
    };
  }>;
}

class OddsService {
  private baseUrl = 'https://sports.core.api.espn.com/v2/sports/football/leagues/nfl';

  async getLiveOdds(gameId: string, competitionId: string): Promise<LiveOdds | null> {
    try {
      const url = `${this.baseUrl}/events/${gameId}/competitions/${competitionId}/odds`;
      console.log(`Fetching odds from: ${url}`);
      
      const response = await fetch(url);
      
      if (!response.ok) {
        console.warn(`No odds available for game ${gameId}: ${response.status}`);
        return null;
      }

      const data: ESPNOddsResponse = await response.json();
      
      if (!data.items || data.items.length === 0) {
        console.warn(`No odds items found for game ${gameId}`);
        return null;
      }

      // Use the first odds provider (usually the primary one)
      const oddsData = data.items[0];
      
      const liveOdds: LiveOdds = {
        gameId,
        competitionId
      };

      // Parse spread data
      if (oddsData.spread !== undefined && oddsData.homeTeamOdds && oddsData.awayTeamOdds) {
        const homeSpread = oddsData.spread;
        const awaySpread = -homeSpread;
        
        liveOdds.spread = {
          home: {
            line: homeSpread,
            odds: oddsData.homeTeamOdds.spreadOdds || -110
          },
          away: {
            line: awaySpread,
            odds: oddsData.awayTeamOdds.spreadOdds || -110
          }
        };
      }

      // Parse moneyline data
      if (oddsData.homeTeamOdds?.moneyLine && oddsData.awayTeamOdds?.moneyLine) {
        liveOdds.moneyline = {
          home: oddsData.homeTeamOdds.moneyLine,
          away: oddsData.awayTeamOdds.moneyLine
        };
      }

      // Parse total (over/under) data
      if (oddsData.overUnder !== undefined) {
        liveOdds.total = {
          over: {
            line: oddsData.overUnder,
            odds: -110 // Default odds, ESPN API doesn't always provide over/under odds
          },
          under: {
            line: oddsData.overUnder,
            odds: -110
          }
        };
      }

      return liveOdds;
    } catch (error) {
      console.error(`Error fetching odds for game ${gameId}:`, error);
      return null;
    }
  }

  async getBatchLiveOdds(games: Array<{ game_id: string; competition_id: string }>): Promise<LiveOdds[]> {
    console.log(`Fetching live odds for ${games.length} games...`);
    
    const oddsPromises = games.map(game => 
      this.getLiveOdds(game.game_id, game.competition_id)
    );

    const results = await Promise.allSettled(oddsPromises);
    
    const liveOdds: LiveOdds[] = [];
    let successCount = 0;
    let errorCount = 0;

    results.forEach((result, index) => {
      if (result.status === 'fulfilled' && result.value) {
        liveOdds.push(result.value);
        successCount++;
      } else {
        errorCount++;
        console.warn(`Failed to get odds for game ${games[index].game_id}`);
      }
    });

    console.log(`Odds fetch complete: ${successCount} successful, ${errorCount} failed`);
    return liveOdds;
  }

  // Helper method to get spread value for a specific team
  getSpreadForTeam(odds: LiveOdds, isHome: boolean): number {
    if (!odds.spread) return 0;
    return isHome ? odds.spread.home.line : odds.spread.away.line;
  }
}

export const oddsService = new OddsService();